/**
 * Billing Slip Modal — thin Dialog wrapper around BillingSlipForm.
 *
 * Used by the FAB (portrait/mobile). The AI Billing Review page uses
 * BillingSlipForm directly in an inline panel.
 */

import { Clock, X } from 'lucide-react';
import Dialog from './Dialog';
import BillingSlipForm, { type BillingSlipFormProps } from './BillingSlipForm';

interface BillingSlipModalProps extends Omit<BillingSlipFormProps, 'onSaved' | 'onCancel'> {
  onClose: () => void;
  onHide?: () => void;
  visible?: boolean;
}

export default function BillingSlipModal({
  onClose, onHide, visible = true,
  ...formProps
}: BillingSlipModalProps) {
  // Dialog stays mounted (preserves BillingSlipForm's timer + state) but its
  // overlay is hidden when not visible. Without this, hiding the slip would
  // unmount the form and reset the timer.
  return (
    <Dialog isOpen={visible} onClose={onClose} ariaLabel="Billing Slip" zIndex={9999}>
      <div style={{
        background: 'var(--card)', borderRadius: 'var(--radius)',
        width: 'min(520px, 96vw)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
        maxHeight: '90vh', overflowY: 'auto',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-light)',
          position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1,
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Clock size={16} style={{ color: 'var(--primary)' }} />
            Billing Slip
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          <BillingSlipForm
            {...formProps}
            onSaved={onClose}
            onCancel={onClose}
            onHide={onHide}
          />
        </div>
      </div>
    </Dialog>
  );
}
